// One bill in full: who it went to, who issued it, and what happened to
// the money against it. Everything shown is the server's own record.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { recordStep, usePageTrail } from "../../../components/chrome/trail";
import { billingStep } from "../lib/billingTrail";
import { downloadInvoicePdf } from "../api/caseInvoices";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { useModuleOn } from "../../../api/attorneyConfig";
import { ModuleOffNotice } from "../../../components/chrome/ModuleOffNotice";
import { RecordPaymentForm, recordedNote } from "../components/RecordPaymentForm";
import { VoidInvoiceButton } from "../components/VoidInvoiceButton";
import { useBillingInvoice } from "../hooks/useBilling";
import { fmtCents } from "../../../lib/money";
import { INVOICE_PILL, METHOD_LABEL, PAYMENT_LABEL, PAYMENT_PILL, day } from "../lib/billingLabels";

const PAYMENT_COLS = "cc-money-cols-bill-payments";

export default function InvoiceDetail() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const q = useBillingInvoice(invoiceId);
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const billingOn = useModuleOn("billing");
  usePageTrail([billingStep(isAdmin), recordStep(q.data?.invoice.number, q.isLoading, "Invoice")]);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading invoice">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={160} radius={10} />
            <Skeleton width="100%" height={90} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  if (q.isError) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-card" style={{ padding: "12px 18px", color: "var(--warn-600)" }}>
            Could not reach the server. Reload to try again.
          </div>
        </div>
      </main>
    );
  }

  if (!q.data) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-card" style={{ padding: "12px 18px", color: "var(--muted)" }}>
            This invoice does not exist - the link may be stale or mistyped.
          </div>
        </div>
      </main>
    );
  }

  const inv = q.data.invoice;
  const payments = q.data.payments;

  return (
    <main className="cc-main">
      <div className="cc-body">
        <ModuleOffNotice id="billing" name="Billing" />
        <div className="cc-index-head">
          <span className="title">
            {inv.number ? <span className="mono">{inv.number}</span> : "Invoice"}
            {"  "}
            <span className={`cc-pill ${INVOICE_PILL[inv.status] ?? "muted"}`}>{inv.status}</span>
          </span>
          <span className="blurb">{inv.description}</span>
        </div>

        <div className="cc-card">
          <dl className="cc-facts">
            <dt>Amount</dt>
            <dd className="tnum">{fmtCents(inv.amountCents)}</dd>
            <dt>Client</dt>
            <dd>{inv.clientLabel ?? "Unknown client"}</dd>
            <dt>Issued</dt>
            <dd className="tnum">{day(inv.createdAt) || "-"}</dd>
            <dt>Due</dt>
            <dd className="tnum">{day(inv.dueDate) || "-"}</dd>
            <dt>Issued by</dt>
            <dd>{inv.issuedByName ?? "-"}</dd>
            <dt>Emailed to client</dt>
            <dd className="tnum">{day(inv.notifiedAt) || "-"}</dd>
            <dt>Invoice</dt>
            <dd>
              <button
                type="button"
                className="cc-link-btn"
                onClick={() => {
                  setDownloadError(null);
                  downloadInvoicePdf(inv._id, inv.number).catch((err) =>
                    setDownloadError(
                      err instanceof Error ? err.message : "Could not download the PDF.",
                    ),
                  );
                }}
              >
                Save invoice PDF
              </button>
            </dd>
          </dl>
          {downloadError && (
            <div
              role="alert"
              style={{ padding: "0 18px 14px", fontSize: 12.5, color: "var(--red-700)" }}
            >
              {downloadError}
            </div>
          )}
        </div>

        <div className="cc-card" style={{ marginTop: 14 }}>
          <div className="cc-card-head">
            <span className="cc-card-title">Payments against this bill</span>
            <div className="actions">
              <span className="cc-card-sub">
                {payments.length === 0
                  ? "None yet"
                  : `${payments.length} attempt${payments.length === 1 ? "" : "s"}`}
              </span>
              {inv.status === "open" && isAdmin && billingOn && (
                <button
                  type="button"
                  className="cc-link-btn"
                  onClick={() => setRecording((v) => !v)}
                >
                  {recording ? "Close form" : "Record payment"}
                </button>
              )}
              {inv.status === "open" && billingOn && (
                <VoidInvoiceButton
                  invoiceId={inv._id}
                  caseId={inv.caseId}
                  number={inv.number}
                  amountCents={inv.amountCents}
                  onVoided={() => {
                    setNote(null);
                    setActionError(null);
                  }}
                  onError={setActionError}
                />
              )}
            </div>
          </div>

          {note && <div className="cc-card-note">{note}</div>}
          {actionError && (
            <div role="alert" className="cc-card-note bad">
              {actionError}
            </div>
          )}

          {billingOn && recording && inv.status === "open" && (
            <RecordPaymentForm
              invoiceId={inv._id}
              caseId={inv.caseId}
              number={inv.number}
              amountCents={inv.amountCents}
              onCancel={() => setRecording(false)}
              onRecorded={(healed) => {
                setRecording(false);
                setNote(recordedNote(healed, inv.number, inv.amountCents));
              }}
            />
          )}
          {payments.length === 0 && (
            <div style={{ padding: "8px 18px 14px", color: "var(--muted)", fontSize: 13 }}>
              No payments have been made or recorded against this bill.
            </div>
          )}
          {payments.length > 0 && (
            <>
              <div className={`cc-money-head ${PAYMENT_COLS}`}>
                <span className="amt">Amount</span>
                <span>How it arrived</span>
                <span>Date</span>
                <span>Status</span>
                <span>Recorded by</span>
              </div>
              {/* Same contract as the /billing ledgers: the whole row
                  navigates, the amount keeps the one real link. */}
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {payments.map((p) => (
                  <li
                    key={p._id}
                    className={`cc-money-row cc-row-lift ${PAYMENT_COLS}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/billing/payment/${p._id}`)}
                  >
                    <span className="amt">
                      <Link
                        className="cc-link-subtle"
                        to={`/billing/payment/${p._id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {fmtCents(p.amountCents)}
                      </Link>
                    </span>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>
                      {p.provider === "manual" ? METHOD_LABEL[p.method ?? "other"] : "Online"}
                      {p.provider === "manual" && p.reference ? (
                        <span className="tnum-mono"> #{p.reference}</span>
                      ) : null}
                      {p.provider === "stripe" && p.providerInvoiceNumber ? (
                        <span className="tnum-mono"> {p.providerInvoiceNumber}</span>
                      ) : null}
                    </span>
                    <span className="tnum" style={{ fontSize: 12, color: "var(--muted)" }}>
                      {day(p.paidAt ?? p.createdAt)}
                    </span>
                    <span style={{ display: "flex", gap: 8 }}>
                      {p.disputeStatus === "open" && <span className="cc-pill crit">Disputed</span>}
                      <span className={`cc-pill ${PAYMENT_PILL[p.status] ?? "muted"}`}>
                        {PAYMENT_LABEL[p.status] ?? p.status}
                      </span>
                    </span>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>
                      {p.recordedByName ?? "-"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
